import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import BackButton from "../components/BackButton";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { clearActowizRequest, fetchActowizFeatures } from "../store/actowiz/actowizSlice";

function FeaturesProducts({ onOpenAuth, onOpenMenu }) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const items = useAppSelector((s) => s.actowiz.features);
  const status = useAppSelector((s) => s.actowiz.featuresStatus);
  const error = useAppSelector((s) => s.actowiz.featuresError);

  const [page, setPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    const start = (page - 1) * pageSize;
    dispatch(
      fetchActowizFeatures({
        start,
        offset: pageSize,
        mode: "replace",
      })
    );
  }, [page, pageSize, dispatch]);

  const getRequestKey = (targetPage) => `features_v2:${(targetPage - 1) * pageSize}:${pageSize}`;
  const hasPrev = page > 1;
  const hasNext = items.length >= pageSize;

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#24303A] flex flex-col">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-10 md:px-6">
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm flex items-end justify-between gap-4">
          <div>
            <BackButton className="mb-3" />
            <h1 className="text-2xl font-extrabold text-[#24303A] md:text-3xl">
              Features Products
            </h1>
            <p className="mt-1 text-sm text-[#6E7B86]">
              Browse featured products.
            </p>
          </div>

          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              disabled={!hasPrev || status === "loading"}
              onClick={() =>
                setPage((v) => {
                  const nextPage = Math.max(1, v - 1);
                  dispatch(clearActowizRequest(getRequestKey(nextPage)));
                  return nextPage;
                })
              }
              className="rounded-full border border-slate-200 bg-white px-3 py-1.5 font-semibold text-slate-700 disabled:opacity-50"
            >
              Prev
            </button>
            <span className="text-slate-500">Page {page}</span>
            <button
              type="button"
              disabled={!hasNext || status === "loading"}
              onClick={() =>
                setPage((v) => {
                  const nextPage = v + 1;
                  dispatch(clearActowizRequest(getRequestKey(nextPage)));
                  return nextPage;
                })
              }
              className="rounded-full bg-[#7352C7] px-3 py-1.5 font-semibold text-white disabled:opacity-50 shadow-sm"
            >
              Next →
            </button>
          </div>
        </div>

        {status === "loading" && (
          <div className="flex justify-center py-16">
            <span className="text-sm text-slate-500">Loading…</span>
          </div>
        )}

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 whitespace-pre-line">
            {error}
          </div>
        )}

        {!error && status !== "loading" && items.length === 0 && (
          <div className="flex justify-center py-16">
            <span className="text-sm text-slate-500">No products found.</span>
          </div>
        )}

        {status !== "loading" && items.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-5">
            {items.map((product, idx) => (
              <ProductCard
                key={`${product.pid || product.sku}-${idx}`}
                accent={["orange", "blue", "green", "purple"][idx % 4]}
                product={product}
                onClick={() =>
                  navigate(`/product/${encodeURIComponent(product.pid || product.sku)}`)
                }
              />
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

export default FeaturesProducts;

